/**
 * Where a signed-in user should land.
 *
 * Custom staff (Member / custom access) only get /dashboard when that
 * leaf is granted. Everyone else keeps the dashboard. Denied staff go
 * to the first nav row they can actually open.
 */

import { isStaffAccessRestricted } from "./staffAccess";
import { STATION_DASHBOARD, stationHref } from "./stationChoice";

export const STAFF_HOME_FALLBACK = "/settings/profile";

function visibleNavChildren(parent, visibility) {
  return (parent.children || []).filter((child) => {
    if (visibility?.[child.id] === false) return false;
    if (child.type === "parent") {
      return visibleNavChildren(child, visibility).length > 0;
    }
    return true;
  });
}

/**
 * First in-app `to` the nav would render.
 * Skips hidden rows, external links, and any path in `skip`.
 */
export function firstVisibleNavPath(items, visibility, { skip = [] } = {}) {
  const skipSet = new Set(skip);
  const walk = (nodes) => {
    for (const item of nodes || []) {
      if (!item || item.external) continue;
      if (visibility?.[item.id] === false) continue;
      if (item.type === "parent") {
        const found = walk(visibleNavChildren(item, visibility));
        if (found) return found;
        continue;
      }
      if (!item.to || item.to === "#" || item.to === "/#") continue;
      const path = String(item.to).split(/[?#]/)[0];
      if (skipSet.has(item.to) || skipSet.has(path)) continue;
      return item.to;
    }
    return null;
  };
  return walk(items);
}

/**
 * `station` is the station this computer was last entered as (DTG Printing,
 * QC, …). Scoped staff on a station PC go straight to it after signing in,
 * so a press operator signs in and is ready to scan. Exit on the station
 * sets it back to the dashboard.
 */
export function resolveStaffHomePath(user, items, visibility, { station } = {}) {
  if (isStaffAccessRestricted(user) && station && station !== STATION_DASHBOARD) {
    const href = stationHref(station);
    if (href) return href;
  }
  if (!isStaffAccessRestricted(user) || visibility?.dashboard !== false) {
    return "/dashboard";
  }
  return (
    firstVisibleNavPath(items, visibility, { skip: ["/dashboard", "/"] }) ||
    STAFF_HOME_FALLBACK
  );
}
